import './ui/styles.css';
import { createShell } from './ui/shell.js';
import { createStartScreen } from './ui/start.js';
import { createErrorScreen } from './ui/error.js';
import { createOverlay } from './debug/overlay.js';
import { installStatusPanel } from './debug/statusPanel.js';
import { button, select } from './debug/controls.js';
import { createScene } from './scene/scene.js';
import { CameraStream } from './camera/stream.js';
import { CameraBackdrop } from './backdrop/CameraBackdrop.js';
import { createAids, SHAPES } from './calibration/aids.js';
import { createCalibration } from './calibration/calibration.js';
import { createPerches } from './calibration/perches.js';
import { createInsectMachine } from './insect/stateMachine.js';
import { species, speciesById } from './data/species.js';
import { createPicker } from './ui/picker.js';
import { createLangToggle } from './ui/langToggle.js';
import { createLabel } from './ui/label.js';
import { createLayers } from './ui/layers.js';
import { ICON_BUTTERFLY } from './ui/icons.js';

const params = new URLSearchParams(location.search);
const app = document.querySelector('#app');
app.classList.add('screen');
const overlay = createOverlay();
const log = overlay.log;
createShell(app);
// Section order in the overlay: Status and Video first, then the tools.
overlay.section('Status');
overlay.section('Video');

const { renderer, scene, camera, stats, render, onResize } = createScene(app, { log });
const stream = new CameraStream({ log });
const backdrop = new CameraBackdrop({ stream, log });
onResize((w, h) => backdrop.resize(w, h));

// App-level state: start | running | error. While running, the insect state
// machine owns app.dataset.state (empty, flyingIn, landing, perched, takingOff).
let appState = 'start';
function setAppState(next) {
  appState = next;
  app.dataset.state = next === 'running' ? insect.state : next;
}

const errorScreen = createErrorScreen(app, {
  onRetry: () => startCamera(),
  detail: () => `state: ${appState} · stream ${stream.status} · recoveries: ${stream.getInfo().recoveries}`,
});
const startScreen = createStartScreen(app, { onStart: () => startCamera() });

async function startCamera() {
  errorScreen.hide();
  const ok = await backdrop.start();
  startScreen.hide();
  if (ok) {
    setAppState('running');
    lockOrientation();
  } else {
    setAppState('error');
    errorScreen.show(stream.status);
  }
  return ok;
}

// The stream re-acquires itself after sleep or a dropped track. If that
// fails (iOS wants a gesture), the error screen provides one.
stream.on('error', ({ kind }) => {
  if (appState === 'start') return;
  setAppState('error');
  errorScreen.show(kind);
});
stream.on('active', () => {
  if (appState === 'error') {
    setAppState('running');
    errorScreen.hide();
  }
});

async function lockOrientation() {
  try {
    await screen.orientation.lock('landscape');
    log('orientation lock: ok');
  } catch (error) {
    log(`orientation lock: ${error.name} (expected on iOS; manifest and Guided Access do it)`);
  }
}

// Calibration (Phase 4) and perches (Phase 5). Aids are the cube, grid and
// axes; markers are the perch dots and normals. Both hidden in kiosk use.
const aids = createAids(scene);
const calibration = createCalibration({ camera, overlay, log });
const perches = createPerches({ scene, camera, canvas: renderer.domElement, overlay, aids, log });
perches.setMarkersVisible(aids.visible);
{
  const aidsButton = button('Aids: ' + (aids.visible ? 'on' : 'off'), () => {
    aids.setVisible(!aids.visible);
    perches.setMarkersVisible(aids.visible);
    aidsButton.textContent = 'Aids: ' + (aids.visible ? 'on' : 'off');
    aidsButton.classList.toggle('on', aids.visible);
  });
  aidsButton.classList.toggle('on', aids.visible);
  const shapeSelect = select(Object.entries(SHAPES).map(([k, v]) => [k, v.label]), aids.shape, (v) => aids.setShape(v));
  overlay.section('Calibration').querySelector('.mw-bar').prepend(aidsButton, shapeSelect);
}

// The butterfly (Phases 6-7) and the visitor UI (Phase 8).
const insect = createInsectMachine({
  scene,
  camera,
  perches,
  log,
  onStateChange: (next) => {
    if (appState === 'running') app.dataset.state = next;
    const perchedId = next === 'perched' || next === 'takingOff' ? insect.speciesId : null;
    picker.setSelected(perchedId);
    layers.setSpecies(next === 'perched' ? insect.speciesId : null);
    if (next === 'perched') label.show(insect.speciesId);
    else label.hide();
  },
});
const picker = createPicker(app, { species, onPick: (id) => insect.request(id) });
const langToggle = createLangToggle(app);
const label = createLabel(app, { speciesById });
const layers = createLayers(app, { speciesById });
{
  const ghost = document.createElement('div');
  ghost.className = 'ghost';
  ghost.setAttribute('aria-hidden', 'true');
  ghost.innerHTML = ICON_BUTTERFLY;
  app.append(ghost);
}
insect.preload(species.map((s) => s.id));
window.addEventListener('pointerdown', () => insect.touch(), { capture: true });
{
  const bar = document.createElement('div');
  bar.className = 'mw-bar';
  for (const s of species) {
    bar.append(button(`Fly ${s.id}`, () => insect.request(s.id)));
    bar.append(button(`Perch ${s.id}`, () => insect.perchNow(s.id)));
  }
  bar.append(button('Dismiss', () => insect.force('takingOff')));
  bar.append(button('Clear', () => insect.force('empty')));
  overlay.section('Insect').append(bar);
  overlay.row('Insect', 'State', () => `${insect.state} ${insect.timeInState.toFixed(1)}s`);
  overlay.row('Insect', 'Species / perch', () => `${insect.speciesId ?? '-'} / ${insect.perch?.id ?? '-'}`);
  overlay.row('Insect', 'Clip', () => insect.butterfly?.currentName ?? '-');
  overlay.row('Insect', 'Flap / bank / u', () => `${insect.stats.flapRate.toFixed(2)} / ${insect.stats.bankDeg.toFixed(0)} / ${insect.stats.pathU.toFixed(2)}`);
  overlay.row('Insect', 'Wing openness', () => (insect.butterfly ? `${insect.butterfly.wingOpenness().toFixed(0)} deg` : '-'));
}

installStatusPanel({ overlay, stats, stream, backdrop, getAppState: () => appState, log });
setAppState('start');

// Handle for the eruda console on the iPad and for automated checks.
window.__mw = {
  insect,
  picker,
  label,
  layers,
  langToggle,
  stream,
  backdrop,
  stats,
  overlay,
  scene,
  camera,
  calibration,
  perches,
  aids,
  get appState() {
    return appState;
  },
};

log(`boot ${__BUILD_TIME__}`);

// Desktop testing convenience: ?autostart=1 skips the tap (no gesture needed
// outside iOS). ?debug=1 opens the overlay.
if (params.get('autostart') === '1') startCamera();

let lastFrame = 0;
function frame(now) {
  const dt = lastFrame ? Math.min(0.1, (now - lastFrame) / 1000) : 0;
  lastFrame = now;
  stream.tick(now);
  backdrop.update(now);
  insect.update(dt, now / 1000);
  render(backdrop.ready ? backdrop : null, now);
  overlay.tick();
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);
